import { useEffect, useState } from "react";
import { ParseResult, parsePlayoutLog } from "./parsePlayoutLog";
import { useLocalStorage } from "usehooks-ts";
import Editor from "react-simple-code-editor";
import exampleInput from "bundle-text:./example.pipl";

export function ValidatorSection() {

    const [input, setInput] = useLocalStorage('pipl-docs.validator.input', exampleInput);
    const [parseResult, setParseResult] = useState<ParseResult>();

    function goToLine(lineNumber: number) {
        // Find the textarea inside the editor
        const textarea = document.querySelector('.validator-input textarea') as HTMLTextAreaElement;
        if (!textarea) return;
        
        // Calculate character position for the start of the line
        const lines = input.split(/\r\n|\n/);
        let charPos = 0;
        for (let i = 0; i < lineNumber - 1 && i < lines.length; i++) {
            charPos += lines[i].length + 1;
        }
        
        // Focus and select the entire line
        textarea.focus();
        textarea.setSelectionRange(charPos, charPos + (lines[lineNumber - 1]?.length || 0));
        
        // Scroll the container to show the line
        const container = document.querySelector('.validator-input-container');
        if (container) {
            const lineHeight = 21;
            container.scrollTop = Math.max(0, (lineNumber - 1) * lineHeight - 50);
        }
    }

    function inputChanged(newInput: string) {
        setInput(newInput);
        setParseResult(parse(newInput))
    }

    function parse(playoutLogInput: string): ParseResult {
        if(!playoutLogInput) {
            return {
                isValid: false,
                commentLines: [],
                validationResults: [],
                items: [],
                delimiter: '\t'
            };
        }

        return parsePlayoutLog(playoutLogInput);
    }

    useEffect(() => {
        inputChanged(input);
    }, [])

    return <div className="validator">
        <h2>Validator</h2>
        <p>
            Paste the contents of your playout log file into the box below. The validator will check the content for any errors and display them below.
        </p>
        <div className="validator-input-container">
        <Editor
            value={input}
            className="validator-input"
            placeholder="Paste your playout log here."
            onValueChange={(evn) => inputChanged(evn)}
            padding={15}
            style={{
                fontSize: 14,
                fontFamily: 'monospace'
            }}
            highlight={(code) => {

                let validationResult = parseResult?.validationResults ?? [];
                let commentLines = parseResult?.commentLines ?? [];
                let delimiter = parseResult?.delimiter ?? '\t';

                // Helper to render line content with highlighted delimiters
                const renderLineWithDelimiters = (line: string, lineIndex: number) => {
                    if (!line) return ' ';
                    const parts = line.split(delimiter);
                    if (parts.length <= 1) return line;
                    
                    return parts.map((part, i) => (
                        <span key={`${lineIndex}-${i}`}>
                            {part}
                            {i < parts.length - 1 && <span className={`delimiter ${delimiter == '\t' ? 'is-tab' : ''}`}>{delimiter}</span>}
                        </span>
                    ));
                };

                // go through each result and highlight the relevant line
                let highlightedCode = code.split(/\r\n|\n/).map((line, index) => {
                    let result = validationResult!.find(r => r.line === index + 1);
                    if(result) {
                        return <div key={index} className={'type-' + result.type}>{renderLineWithDelimiters(line, index)}</div>;
                    } else {
                        if(commentLines.includes(index + 1)) {
                            return <div key={index} className={'type-comment'}>{line || ' '}</div>;
                        } else {
                            return <div key={index}>{renderLineWithDelimiters(line, index) || ' '}</div>;
                        }
                    }
                });
                
                return highlightedCode;
            }}
            tabSize={1}
            insertSpaces={false}
            data-gramm="false"
            data-gramm_editor="false"
            data-enable-grammarly="false"
            />
        </div>
        {(parseResult?.validationResults ?? []).length > 0 && <>
            <p>
                Validation:
            </p>
            <ul className="validator-results">
                {parseResult?.validationResults.map((result, index) => (
                    <li key={index} className={'type-' + result.type}>
                        {result.line && <button onClick={() => goToLine(result.line!)}>Line {result.line}</button>}
                        <span>{result.message}</span>
                    </li>
                ))}
            </ul>
        </>}

        {(parseResult?.items ?? []).length > 0 && <>
        <p>
            Items:
        </p>
        <div className="items-container">
            <table className="items">
                <thead>
                    <tr>
                        <th>Hour</th>
                        <th>Start Time</th>
                        <th>Details</th>
                        <th>Reconciliation ID</th>
                        <th>Segue Info</th>
                    </tr>
                </thead>
                <tbody>
                    {parseResult?.items.map((item, index) => <tr key={index}>
                        <td>{item.hour}</td>
                        <td>{item.startTime}</td>
                        <td style={{ textAlign: 'left' }}>
                            {item.trackId && <span>Track: {item.trackId}</span>}
                            {item.specialItemConfiguration && <span>Special: {item.specialItemConfiguration}</span>}
                        </td>
                        <td>{item.reconciliationId}</td>
                        <td>{(item.segueInfo != null ? '<present>' : '')}</td>
                    </tr>)}
                </tbody>
            </table>
        </div>
        </>}
    </div>;
}
